<script setup lang="ts">
import { ChevronRight, Sparkles } from 'lucide-vue-next';
import EntityRow from '@/components/workspace/EntityRow.vue';
import SectionSurface from '@/components/workspace/SectionSurface.vue';
import type { WorkspaceAttentionItem } from '~/types/workspaceAttention';

withDefaults(defineProps<{
    items: WorkspaceAttentionItem[];
    eyebrow?: string;
    title?: string;
    description?: string;
    briefing?: string;
}>(), {
    eyebrow: 'Overview',
    title: 'What matters right now?',
    description: 'Priority actions and work waiting for a response.',
});

const emit = defineEmits<{
    open: [itemId: string];
}>();
</script>

<template>
    <SectionSurface
        :eyebrow="eyebrow"
        :title="title"
        :description="description"
        :icon="Sparkles"
        density="compact"
        presentation="editorial"
        class="workspace-attention-focus"
    >
        <template #icon>
            <svg aria-hidden="true" class="absolute size-0">
                <defs>
                    <linearGradient id="workspace-attention-icon-gradient" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stop-color="light-dark(var(--brand-primary), var(--brand-dark-primary))" />
                        <stop offset="100%" stop-color="light-dark(var(--brand-accent), var(--brand-dark-accent))" />
                    </linearGradient>
                </defs>
            </svg>
            <Sparkles class="size-3.5" style="stroke: url(#workspace-attention-icon-gradient)" />
        </template>

        <template v-if="$slots.actions" #actions>
            <slot name="actions" />
        </template>

        <p v-if="briefing" class="workspace-attention-focus__briefing mb-3 border-l-2 pl-3 text-sm leading-6 text-muted-foreground">
            {{ briefing }}
        </p>

        <EntityRow
            v-for="item in items"
            :key="item.id"
            :title="item.title"
            :description="item.detail"
            :icon="item.icon"
            interactive
            @activate="emit('open', item.id)"
        >
            <template #leading>
                <component :is="item.icon" class="size-4" :class="item.accentClass" />
            </template>
            <template #trailing>
                <span class="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                    {{ item.actionLabel || 'Review' }}
                    <ChevronRight class="size-3.5" />
                </span>
            </template>
        </EntityRow>
    </SectionSurface>
</template>
